import { Flex, Pagination, styled, Table } from '@conteg/ui';
import {
  createColumnHelper,
  getCoreRowModel,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { AuditEventDto, SortOrder } from 'types/generated/graphql';
import { formatDateTime } from 'utils/format/format';

import type { AuditView } from '../types';
import { PaginationMeta } from '../types';
import { DetailPreviewCard } from './detail-preview-card';
import { getEventDetails, getMetadata } from './event-details-mapper';

/** OXIT-718: the name each commercial-invoice outcome reads as in the list. */
const COMMERCIAL_INVOICE_EVENT_NAMES: Record<string, string> = {
  invoiceattached:
    'Events.Descriptions.Reservation.SubmissionCommercialInvoiceAttached',
  invoicemissing:
    'Events.Descriptions.Reservation.SubmissionCommercialInvoiceMissing',
  customerleft:
    'Events.Descriptions.Reservation.SubmissionCommercialInvoiceCustomerLeft',
  checkfailednotasked:
    'Events.Descriptions.Reservation.SubmissionCommercialInvoiceNotAsked',
  notrequired:
    'Events.Descriptions.Reservation.SubmissionCommercialInvoiceNotRequired',
};

/** Outcomes where the drop-off did not go as intended. A carrier wanting no invoice is normal. */
const COMMERCIAL_INVOICE_FAILED_OUTCOMES = new Set([
  'invoicemissing',
  'customerleft',
  'checkfailednotasked',
]);

const tableColumnHelper = createColumnHelper<AuditEventDto>();

const DetailLabel = styled.span`
  font-weight: 600;
  margin-right: 0.5rem;
`;

const TimeCell = styled.div`
  width: 100px;
  min-width: 100px;
  max-width: 100px;
`;

const EventNameCell = styled.span`
  display: flex;
  align-items: center;
  gap: 1.5rem;
`;

/**
 * Get emoji icon for event subject based on prefix
 */
function getEventEmoji(eventSubject: string | null | undefined): string {
  if (!eventSubject) return '';

  const prefix = eventSubject.split('.')[0];

  switch (prefix) {
    case 'Point':
      return '📍';
    case 'Content':
      return '📦';
    case 'StorageUnit':
      return '🗄️';
    case 'Courier':
      return '🚚';
    case 'Customer':
      return '👤';
    case 'Payment':
      return '💳';
    case 'Allocation':
      return '📥';
    case 'Reservation':
      return '📅';
    case 'Printer':
      return '🖨️';
    default:
      return '📋';
  }
}

interface EventsTableProps {
  events: AuditEventDto[];
  loading?: boolean;
  pagination: PaginationMeta;
  onPageChange?: (page: number) => void;
  sortOrder: SortOrder;
  onSortChange?: (sortOrder: SortOrder) => void;
  view?: AuditView;
}

/**
 * A two-factor code entry has five outcomes on one subject, and they are not interchangeable: a refusal
 * must not read like a success, an unavailable check did not spend an attempt, and the last two mean the
 * parcel is already on its way back to the sender.
 */
const TWO_FACTOR_OUTCOME_LABELS: Record<
  string,
  { key: string; emoji: string }
> = {
  Verified: {
    key: 'Events.Descriptions.Customer.TwoFactorVerified',
    emoji: '✅',
  },
  Rejected: {
    key: 'Events.Descriptions.Customer.TwoFactorRejected',
    emoji: '❌',
  },
  Unavailable: {
    key: 'Events.Descriptions.Customer.TwoFactorUnavailable',
    emoji: '⚠️',
  },
  ReturnedToSender: {
    key: 'Events.Descriptions.Customer.TwoFactorReturnedToSender',
    emoji: '↩️',
  },
  AlreadyReturned: {
    key: 'Events.Descriptions.Customer.TwoFactorAlreadyReturned',
    emoji: '⛔',
  },
};

const createTableColumns = (t: TFunction, view?: AuditView) => [
  tableColumnHelper.accessor('localTime', {
    header: () => t('Events.Table.Header.Time'),
    cell: (info) => {
      return <TimeCell>{formatDateTime(info.getValue())}</TimeCell>;
    },
    enableSorting: true,
  }),
  tableColumnHelper.accessor('eventSubject', {
    header: () => t('Events.Table.Header.EventName'),
    cell: (info) => {
      const event = info.row.original;
      const eventSubject = info.getValue() || event.messageName;

      if (!eventSubject) return null;

      // Try to get translation, fallback to eventSubject if not found
      const translationKey = `Events.Descriptions.${eventSubject}`;
      let translatedName = t(translationKey, { defaultValue: eventSubject });

      // Reservation.Deallocated covers two different flows - name the event by the
      // actual deallocation reason instead of a one-size-fits-all label (OXIT-52).
      if (
        eventSubject === 'Reservation.Deallocated' &&
        event.reservationDeallocationReason
      ) {
        translatedName = t(
          `Events.Descriptions.Reservation.Deallocated.${event.reservationDeallocationReason}`
        );
      }

      let emoji = getEventEmoji(eventSubject);

      // Point online/offline is a single subject with two outcomes - show the actual state. The
      // icon carries it too: this view exists to spot outages in a list of otherwise identical pins.
      if (eventSubject === 'Point.StatusOnlineReported') {
        const isOnline =
          event.metadata
            ?.find((m) => m.key === 'IsOnline')
            ?.value?.toLowerCase() === 'true';
        translatedName = isOnline
          ? t('Events.Descriptions.Point.Online')
          : t('Events.Descriptions.Point.Offline');
        emoji = isOnline ? '🟢' : '🔴';
      }

      // A failed verification must not read as a success: give it its own name and emoji
      // instead of the neutral "Zákazník se ověřil" / "Zákazník ověřen PINem".
      const isFailedVerification =
        getMetadata(event, 'IsSuccessful')?.toLowerCase() === 'false';

      if (
        eventSubject === 'Reservation.SubmissionCodeValidated' &&
        isFailedVerification
      ) {
        translatedName = t(
          'Events.Descriptions.Reservation.SubmissionCodeValidatedFailed'
        );
        emoji = '❌';
      }

      if (eventSubject === 'Customer.PinVerified' && isFailedVerification) {
        translatedName = t('Events.Descriptions.Customer.PinVerifiedFailed');
        emoji = '❌';
      }

      if (eventSubject === 'Courier.CodeVerified' && isFailedVerification) {
        translatedName = t('Events.Descriptions.Courier.CodeVerifiedFailed');
        emoji = '❌';
      }

      // OXIT-703: a receipt that never went out must not read like one that did - the operator is
      // scanning this list precisely for the undelivered ones. Both non-accepted outcomes get their
      // own name, because "the carrier refused" and "we never reached the carrier" send them after
      // different culprits.
      if (eventSubject === 'Reservation.SubmissionReceiptRequested') {
        const receiptOutcome = getMetadata(
          event,
          'ReceiptOutcome'
        )?.toLowerCase();

        if (receiptOutcome === 'rejectedbycarrier') {
          translatedName = t(
            'Events.Descriptions.Reservation.SubmissionReceiptRejected'
          );
          emoji = '❌';
        }

        if (receiptOutcome === 'carriercallfailed') {
          translatedName = t(
            'Events.Descriptions.Reservation.SubmissionReceiptCallFailed'
          );
          emoji = '❌';
        }
      }

      // OXIT-718: the subject's own name says only that the question was asked - an outcome this
      // version does not know keeps it rather than reading as a confirmation that never happened.
      if (eventSubject === 'Reservation.SubmissionCommercialInvoiceChecked') {
        const invoiceOutcome = getMetadata(
          event,
          'CommercialInvoiceOutcome'
        )?.toLowerCase();

        const invoiceName =
          COMMERCIAL_INVOICE_EVENT_NAMES[invoiceOutcome ?? ''];

        if (invoiceName) {
          translatedName = t(invoiceName);
          if (COMMERCIAL_INVOICE_FAILED_OUTCOMES.has(invoiceOutcome ?? ''))
            emoji = '❌';
        }
      }

      const isScanned = getMetadata(event, 'IsScanned')?.toLowerCase();

      // Distinguish manual code entry from a scan; missing IsScanned means the code was
      // scanned (events predating the flag), so keep the default "naskenoval" name.
      // A failed verification keeps its ❌ name — the result matters more than the method.
      if (isScanned === 'false' && !isFailedVerification) {
        if (eventSubject === 'Customer.CodeScanReceived') {
          translatedName = t(
            'Events.Descriptions.Customer.CodeScanReceivedManual'
          );
          emoji = '⌨️';
        } else if (eventSubject === 'Courier.CodeScanReceived') {
          translatedName = t(
            'Events.Descriptions.Courier.CodeScanReceivedManual'
          );
          emoji = '⌨️';
        } else if (eventSubject === 'Customer.PinVerified') {
          translatedName = t('Events.Descriptions.Customer.PinVerifiedManual');
          emoji = '⌨️';
        } else if (eventSubject === 'Courier.LoggedIn') {
          translatedName = t('Events.Descriptions.Courier.LoggedInManual');
          emoji = '⌨️';
        } else if (eventSubject === 'Courier.CodeVerified') {
          // Like the CodeScanReceived events, this one's default name already says "naskenováním",
          // so only the manual variant needs naming here.
          translatedName = t('Events.Descriptions.Courier.CodeVerifiedManual');
          emoji = '⌨️';
        }
      }

      // One subject carries all five outcomes of a two-factor code entry, and a refusal must not read
      // like a success. An unknown outcome keeps the neutral subject label rather than guessing.
      if (eventSubject === 'Customer.TwoFactorVerificationAttempted') {
        const outcome = getMetadata(event, 'TwoFactorOutcome');
        const named = outcome ? TWO_FACTOR_OUTCOME_LABELS[outcome] : undefined;

        if (named) {
          translatedName = t(named.key);
          emoji = named.emoji;
        }
      }

      if (eventSubject === 'Reservation.TwoFactorBypassed') {
        const bypassOutcome = getMetadata(event, 'TwoFactorBypassOutcome');

        // Both names are set here and the subject keeps a neutral label of its own: a row whose
        // outcome is missing or unrecognised must not fall back to reading as "released", and the subject
        // also names the entry in the event-type filter, where it covers refusals too.
        if (bypassOutcome === 'Refused') {
          translatedName = t(
            'Events.Descriptions.Reservation.TwoFactorBypassRefused'
          );
          emoji = '🚫';
        } else if (bypassOutcome === 'Released') {
          translatedName = t(
            'Events.Descriptions.Reservation.TwoFactorBypassReleased'
          );
          emoji = '⚠️';
        }
      }

      // These two events have a method-neutral default name, unlike the CodeScanReceived events
      // whose default already implies a scan. So the scan variant has to be named explicitly here,
      // and an event without the flag keeps the neutral name rather than being relabelled.
      if (isScanned === 'true' && !isFailedVerification) {
        if (eventSubject === 'Customer.PinVerified') {
          translatedName = t('Events.Descriptions.Customer.PinVerifiedScanned');
        } else if (eventSubject === 'Courier.LoggedIn') {
          translatedName = t('Events.Descriptions.Courier.LoggedInScanned');
        }
      }

      return (
        <EventNameCell>
          <span>{emoji}</span> <span>{translatedName}</span>
        </EventNameCell>
      );
    },
    enableSorting: false,
  }),
  tableColumnHelper.display({
    id: 'details',
    header: () => t('Events.Table.Header.Details'),
    cell: (info) => {
      const event = info.row.original;
      const details = getEventDetails(event, t, view);

      if (details.length === 0) return null;

      return (
        <div>
          {details
            .filter((detail) => detail.value) // Only show details with values
            .map((detail, i) => (
              <div key={i}>
                {detail.displayType === 'link' ? (
                  <a
                    href={detail.value!}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {detail.label}
                  </a>
                ) : (
                  <>
                    <DetailLabel>{detail.label}:</DetailLabel>
                    {detail.displayType === 'preview' ? (
                      <DetailPreviewCard content={detail.value!} />
                    ) : (
                      detail.value
                    )}
                  </>
                )}
              </div>
            ))}
        </div>
      );
    },
    enableSorting: false,
  }),
];

/**
 * Reusable events table component
 * Displays audit events with detailed information
 */
export function EventsTable({
  events,
  loading,
  pagination,
  onPageChange,
  sortOrder,
  onSortChange,
  view,
}: EventsTableProps) {
  const { t } = useTranslation();

  const tableColumns = createTableColumns(t, view);

  // Convert SortOrder enum to TanStack Table sorting state
  const sorting: SortingState = [
    {
      id: 'localTime',
      desc: sortOrder === SortOrder.Descending,
    },
  ];

  const eventsTable = useReactTable({
    data: events,
    columns: tableColumns,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    manualSorting: true,
    enableSorting: true,
    enableSortingRemoval: false,
    state: {
      sorting,
    },
    onSortingChange: (updater) => {
      const newSorting =
        typeof updater === 'function' ? updater(sorting) : updater;

      if (newSorting.length > 0 && newSorting[0].id === 'localTime') {
        const newSortOrder = newSorting[0].desc
          ? SortOrder.Descending
          : SortOrder.Ascending;
        onSortChange?.(newSortOrder);
      }
    },
  });

  return (
    <Flex
      flexDirection="column"
      gap="2rem"
      backgroundColor="white"
      padding="4rem"
      borderRadius="2.5rem"
    >
      <Table
        table={eventsTable}
        isEmpty={events.length === 0}
        isLoading={loading}
        emptyState={{
          title: t('Events.Table.EmptyState.Title'),
          description: t('Events.Table.EmptyState.Description'),
        }}
      />

      {events.length > 0 && (
        <Flex justifyContent="space-between" alignItems="center">
          <div style={{ color: '#6b7280' }}>
            {t('Events.Table.Pagination', {
              from: (pagination.currentPage - 1) * pagination.itemsPerPage + 1,
              to: Math.min(
                pagination.currentPage * pagination.itemsPerPage,
                pagination.totalItems
              ),
              total: pagination.totalItems,
            })}
          </div>
          <Pagination
            hideForOnlyOnePage
            activePage={pagination.currentPage - 1}
            pagesCount={pagination.totalPages}
            onChange={(pageIndex) => onPageChange?.(pageIndex + 1)}
          />
        </Flex>
      )}
    </Flex>
  );
}
